import QRCode from 'qrcode'
import {
  TYPE_INTERPRETATIONS,
  TYPE_COLORS,
  getShareCardContent,
  type TypeCode,
} from '@mindtype/shared'

interface DimensionData {
  leftLabel: string
  rightLabel: string
  percentage: number // 0-100, 0=左侧, 100=右侧
}

interface ShareImageData {
  typeCode: TypeCode
  dimensions: DimensionData[]
}

type CardStyle = 'simple' | 'standard' | 'detailed'

export async function generateShareImage(
  data: ShareImageData,
  cardStyle: CardStyle = 'standard'
): Promise<Blob> {
  const scale = 2
  const width = 375

  // 根据卡片样式计算高度
  const getHeight = () => {
    switch (cardStyle) {
      case 'simple':
        return 400
      case 'standard':
        return 500
      case 'detailed':
        return 600
    }
  }
  const height = getHeight()

  const canvas = document.createElement('canvas')
  canvas.width = width * scale
  canvas.height = height * scale

  const ctx = canvas.getContext('2d')!
  ctx.scale(scale, scale)

  const fontFamily =
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif'

  const typeCode = data.typeCode
  const typeInfo = TYPE_INTERPRETATIONS[typeCode]
  const colorConfig = TYPE_COLORS[typeCode]
  const shareContent = getShareCardContent(typeCode)
  const bestMatchColor = TYPE_COLORS[shareContent.bestMatch]
  const oppositeMatchColor = TYPE_COLORS[shareContent.oppositeMatch]

  // 1. 背景
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, width, height)

  // 2. 装饰圆形
  drawDecorativeCircles(ctx, width, colorConfig.primary, colorConfig.secondary)

  // 3. Logo
  ctx.fillStyle = '#1f2937'
  ctx.font = `bold 14px ${fontFamily}`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText('MindType', width / 2, 32)

  ctx.fillStyle = '#6b7280'
  ctx.font = `10px ${fontFamily}`
  ctx.fillText('认知风格测评', width / 2, 48)

  // 4. 类型展示区背景
  const typeAreaY = 65
  const typeAreaHeight = 115
  drawRoundedRect(
    ctx,
    16,
    typeAreaY,
    width - 32,
    typeAreaHeight,
    12,
    hexToRgba(colorConfig.primary, 0.1)
  )

  // 5. "你的认知类型是"
  ctx.fillStyle = '#4b5563'
  ctx.font = `10px ${fontFamily}`
  ctx.fillText('你的认知类型是', width / 2, typeAreaY + 20)

  // 6. 类型代码框
  const codeBoxWidth = 100
  const codeBoxHeight = 36
  const codeBoxX = (width - codeBoxWidth) / 2
  const codeBoxY = typeAreaY + 32

  drawRoundedRect(ctx, codeBoxX, codeBoxY, codeBoxWidth, codeBoxHeight, 6, colorConfig.primary)

  ctx.fillStyle = colorConfig.text
  ctx.font = `bold 20px ${fontFamily}`
  ctx.fillText(typeCode, width / 2, codeBoxY + codeBoxHeight / 2)

  // 7. 类型名称
  ctx.fillStyle = colorConfig.primary
  ctx.font = `bold 16px ${fontFamily}`
  ctx.fillText(typeInfo?.name || typeCode, width / 2, codeBoxY + codeBoxHeight + 20)

  // 8. Tagline
  ctx.fillStyle = '#4b5563'
  ctx.font = `10px ${fontFamily}`
  ctx.fillText(`「${typeInfo?.tagline || ''}」`, width / 2, codeBoxY + codeBoxHeight + 38)

  let currentY = typeAreaY + typeAreaHeight + 16

  // 9. 行为倾向 - 标准版和详细版显示
  if (cardStyle === 'standard' || cardStyle === 'detailed') {
    ctx.textAlign = 'left'

    shareContent.behaviorTags.forEach((trait) => {
      // 背景
      drawRoundedRect(
        ctx,
        16,
        currentY,
        width - 32,
        28,
        8,
        hexToRgba(colorConfig.primary, 0.05)
      )

      // 圆点
      ctx.beginPath()
      ctx.arc(28, currentY + 14, 3, 0, Math.PI * 2)
      ctx.fillStyle = colorConfig.primary
      ctx.fill()

      // 文字
      ctx.fillStyle = '#374151'
      ctx.font = `11px ${fontFamily}`
      ctx.textBaseline = 'middle'
      ctx.fillText(trait, 40, currentY + 14)

      currentY += 34
    })

    currentY += 4
  }

  // 10. 四维度分析 - 仅详细版显示
  if (cardStyle === 'detailed') {
    // 背景卡片
    const dimAreaHeight = 110
    drawRoundedRect(ctx, 16, currentY, width - 32, dimAreaHeight, 12, '#f9fafb')

    // 标题
    ctx.fillStyle = '#6b7280'
    ctx.font = `9px ${fontFamily}`
    ctx.textAlign = 'center'
    ctx.fillText('四维度分析', width / 2, currentY + 16)

    // 四个维度
    let dimY = currentY + 32
    const lineStartX = 50
    const lineEndX = width - 50
    const lineWidth = lineEndX - lineStartX

    data.dimensions.forEach((dim) => {
      // 左侧标签
      ctx.fillStyle = '#6b7280'
      ctx.font = `9px ${fontFamily}`
      ctx.textAlign = 'right'
      ctx.textBaseline = 'middle'
      ctx.fillText(dim.leftLabel, lineStartX - 8, dimY)

      // 右侧标签
      ctx.textAlign = 'left'
      ctx.fillText(dim.rightLabel, lineEndX + 8, dimY)

      // 线条
      ctx.beginPath()
      ctx.moveTo(lineStartX, dimY)
      ctx.lineTo(lineEndX, dimY)
      ctx.strokeStyle = '#d1d5db'
      ctx.lineWidth = 2
      ctx.stroke()

      // 指示点
      const dotX = lineStartX + (lineWidth * dim.percentage) / 100
      ctx.beginPath()
      ctx.arc(dotX, dimY, 5, 0, Math.PI * 2)
      ctx.fillStyle = colorConfig.primary
      ctx.fill()
      // 白色边框
      ctx.beginPath()
      ctx.arc(dotX, dimY, 5, 0, Math.PI * 2)
      ctx.strokeStyle = '#ffffff'
      ctx.lineWidth = 2
      ctx.stroke()

      dimY += 20
    })

    currentY += dimAreaHeight + 8
  }

  // 11. 最佳拍档 / 欢喜冤家 - 标准版和详细版显示
  if (cardStyle === 'standard' || cardStyle === 'detailed') {
    // 背景卡片
    const matchAreaHeight = 75
    drawRoundedRect(ctx, 16, currentY, width - 32, matchAreaHeight, 12, '#f9fafb')

    const leftX = width / 4 + 8
    const rightX = (width * 3) / 4 - 8

    // 最佳拍档
    ctx.fillStyle = '#6b7280'
    ctx.font = `9px ${fontFamily}`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText('最佳拍档', leftX, currentY + 14)

    // 标签
    const badgeWidth = 50
    const badgeHeight = 22
    drawRoundedRect(
      ctx,
      leftX - badgeWidth / 2,
      currentY + 22,
      badgeWidth,
      badgeHeight,
      4,
      bestMatchColor.primary
    )
    ctx.fillStyle = bestMatchColor.text
    ctx.font = `bold 11px ${fontFamily}`
    ctx.fillText(shareContent.bestMatch, leftX, currentY + 22 + badgeHeight / 2)

    // 类型名称
    ctx.fillStyle = bestMatchColor.primary
    ctx.font = `9px ${fontFamily}`
    ctx.fillText(
      TYPE_INTERPRETATIONS[shareContent.bestMatch]?.name || '',
      leftX,
      currentY + 22 + badgeHeight + 12
    )

    // 分隔线
    ctx.beginPath()
    ctx.moveTo(width / 2, currentY + 10)
    ctx.lineTo(width / 2, currentY + matchAreaHeight - 10)
    ctx.strokeStyle = '#e5e7eb'
    ctx.lineWidth = 1
    ctx.stroke()

    // 欢喜冤家
    ctx.fillStyle = '#6b7280'
    ctx.font = `9px ${fontFamily}`
    ctx.fillText('欢喜冤家', rightX, currentY + 14)

    // 标签
    drawRoundedRect(
      ctx,
      rightX - badgeWidth / 2,
      currentY + 22,
      badgeWidth,
      badgeHeight,
      4,
      oppositeMatchColor.primary
    )
    ctx.fillStyle = oppositeMatchColor.text
    ctx.font = `bold 11px ${fontFamily}`
    ctx.fillText(shareContent.oppositeMatch, rightX, currentY + 22 + badgeHeight / 2)

    // 类型名称
    ctx.fillStyle = oppositeMatchColor.primary
    ctx.font = `9px ${fontFamily}`
    ctx.fillText(
      TYPE_INTERPRETATIONS[shareContent.oppositeMatch]?.name || '',
      rightX,
      currentY + 22 + badgeHeight + 12
    )

    currentY += matchAreaHeight + 8
  }

  // 12. QR 码
  const qrSize = 48
  const qrY = currentY + 4
  await drawQRCode(ctx, 'https://mindtype.pages.dev', width / 2 - qrSize / 2, qrY, qrSize)

  // 13. 底部文字
  ctx.fillStyle = '#6b7280'
  ctx.font = `9px ${fontFamily}`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText('测测你的认知风格', width / 2, qrY + qrSize + 12)

  // 14. 底部装饰
  drawBottomDecoration(ctx, width, height, colorConfig.primary, colorConfig.secondary)

  // 导出
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob!), 'image/png')
  })
}

function drawDecorativeCircles(
  ctx: CanvasRenderingContext2D,
  width: number,
  primary: string,
  secondary: string
) {
  // 右上角大圆
  ctx.beginPath()
  ctx.arc(width + 10, -10, 50, 0, Math.PI * 2)
  ctx.fillStyle = hexToRgba(primary, 0.1)
  ctx.fill()

  // 左上角小圆
  ctx.beginPath()
  ctx.arc(20, 20, 12, 0, Math.PI * 2)
  ctx.fillStyle = hexToRgba(secondary, 0.08)
  ctx.fill()
}

function drawBottomDecoration(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  primary: string,
  secondary: string
) {
  // 左下角大圆
  ctx.beginPath()
  ctx.arc(-10, height + 10, 40, 0, Math.PI * 2)
  ctx.fillStyle = hexToRgba(secondary, 0.08)
  ctx.fill()

  // 右下角小圆
  ctx.beginPath()
  ctx.arc(width - 15, height - 15, 8, 0, Math.PI * 2)
  ctx.fillStyle = hexToRgba(primary, 0.1)
  ctx.fill()
}

function drawRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
  fill: string
) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.lineTo(x + w - r, y)
  ctx.quadraticCurveTo(x + w, y, x + w, y + r)
  ctx.lineTo(x + w, y + h - r)
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h)
  ctx.lineTo(x + r, y + h)
  ctx.quadraticCurveTo(x, y + h, x, y + h - r)
  ctx.lineTo(x, y + r)
  ctx.quadraticCurveTo(x, y, x + r, y)
  ctx.closePath()
  ctx.fillStyle = fill
  ctx.fill()
}

async function drawQRCode(
  ctx: CanvasRenderingContext2D,
  url: string,
  x: number,
  y: number,
  size: number
) {
  const qrCanvas = document.createElement('canvas')
  await QRCode.toCanvas(qrCanvas, url, {
    width: size,
    margin: 0,
    color: { dark: '#374151', light: '#ffffff' },
  })
  ctx.drawImage(qrCanvas, x, y, size, size)
}

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}
